import React from "react";

/**
 * Unified page hero — the ONE header treatment used across every page.
 *
 * Master Spec (§1.1–§1.4, §1.8):
 *   • Eyebrow: 12px / 600 / uppercase / 0.08em / ACCENT (#F97316) — with a
 *     5px solid accent dot before it. No pulse animation.
 *   • Title:   FIXED 34px / 700 / near-black (#0B1220) / -0.02em / 1.15
 *              (no clamp — every H1 across the product must be identical).
 *              On mobile (max-width 640px) the title steps down to 26px.
 *   • Sub:     15px / 400 / muted #6B7280 / 1.6 / max-width 600px.
 *
 * Variants:
 *   • `variant="card"` (default) — bordered white card. Original treatment.
 *   • `variant="bare"`           — no border, no radius. Sits flush against
 *                                  the page. Use on Directory + Pricing.
 *   • `size="sm"`                — smaller 24px title. For tool detail,
 *                                  payment success, auth-callback style
 *                                  screens that need a compact header.
 *
 * `children` renders below the sub — the natural slot for stat pills,
 * chips, or supplementary rows.
 */
export function PageHero({
  eyebrow,
  title,
  sub,
  badge,
  children,
  variant = "card",
  size = "lg",
  testId = "page-hero",
}) {
  const isBare = variant === "bare";
  const isSm = size === "sm";
  const outer = isBare
    ? "relative overflow-hidden mb-6"
    : "relative overflow-hidden rounded-2xl bg-white border border-[#E7EAF0] mb-6";
  const inner = isBare
    ? "relative z-10 py-8 sm:py-10 max-w-[720px]"
    : "relative z-10 px-6 sm:px-9 py-8 sm:py-9 max-w-[720px]";
  const titleClass = isSm
    ? "wf-h2 mb-2"
    : "wf-h1 wf-hero-title mb-3";

  return (
    <section className={outer} data-testid={testId}>
      {badge && (
        <span
          className="absolute top-4 right-4 z-20 wf-pill bg-[#F7F8FA] border border-[#E7EAF0] text-[#5B6478]"
          data-testid={`${testId}-badge`}
        >
          {badge}
        </span>
      )}
      <div className={inner}>
        {eyebrow && (
          <div
            className="inline-flex items-center gap-1.5 text-[12px] font-semibold uppercase tracking-[0.08em] text-[#F97316] mb-3"
            data-testid={`${testId}-eyebrow`}
          >
            <span className="inline-block w-[5px] h-[5px] rounded-full bg-[#F97316]" aria-hidden="true"/>
            <span>{eyebrow}</span>
          </div>
        )}
        {title && (
          <h1 className={titleClass} data-testid={`${testId}-title`}>
            {title}
          </h1>
        )}
        {sub && (
          <p
            className="text-[15px] text-[#6B7280] leading-[1.6] max-w-[600px]"
            data-testid={`${testId}-sub`}
          >
            {sub}
          </p>
        )}
        {children && <div className="mt-5">{children}</div>}
      </div>
    </section>
  );
}
